/**
 * createSchoolAssessmentFormatter — pure helpers for the add-assessment create draft.
 *
 * Lives in `ports/primary/ui/formatters/` (sibling of `ui/types/`), outside `design-system/`.
 * No `@data` / store / service deps — Storybook-safe.
 */
import moment from "moment"

import { type UICreateSchoolAssessment, type UISchoolAssessmentPreview } from "../types/createSchoolAssessment"
import { type UISchoolAssessment } from "../types/schoolAssessment"

/** Empty create draft — default exam date is +7 days (`YYYY-MM-DD`). */
export function emptyCreateSchoolAssessment(): UICreateSchoolAssessment {
    return {
        subjectId: null,
        date: moment().add(7, "days").format("YYYY-MM-DD"),
        chapterIds: [],
        customChapters: [],
        keyRequirementsText: "",
        aiPreview: null,
    }
}

/**
 * Prefill the create/edit draft from a listed `UISchoolAssessment`.
 * Drops the synthetic progress row (chapter id === assessment id) used when the API
 * only had overall `preparation_progress`.
 */
export function draftFromUISchoolAssessment(assessment: UISchoolAssessment): UICreateSchoolAssessment {
    const chapterIds = assessment.chaptersProgress
        .map((row) => row.chapter.id)
        .filter((id) => String(id) !== String(assessment.id))

    return {
        subjectId: assessment.subject.id,
        date: moment(assessment.date).format("YYYY-MM-DD"),
        chapterIds,
        customChapters: [],
        keyRequirementsText: assessment.keyRequirements ?? "",
        aiPreview: null,
    }
}

/** True when subject + at least one chapter (catalog or custom) are set. */
export function canSubmitCreateSchoolAssessment(draft: UICreateSchoolAssessment): boolean {
    return draft.subjectId != null && (draft.chapterIds.length > 0 || draft.customChapters.length > 0)
}

/**
 * Merge an AI preview into the create draft (subject, chapters, date, key requirements).
 * Keeps `aiPreview` on the draft so the media step can still show the success card.
 */
export function applySchoolAssessmentPreview(
    draft: UICreateSchoolAssessment,
    preview: UISchoolAssessmentPreview
): UICreateSchoolAssessment {
    const date = preview.date != null && preview.date !== "" ? moment(preview.date).format("YYYY-MM-DD") : draft.date

    return {
        ...draft,
        subjectId: preview.subject?.id ?? draft.subjectId,
        chapterIds: preview.chapters?.map((c) => c.id) ?? draft.chapterIds,
        date,
        keyRequirementsText:
            preview.keyRequirements != null && preview.keyRequirements.length > 0
                ? preview.keyRequirements.join("\n")
                : draft.keyRequirementsText,
        aiPreview: preview,
    }
}
